import React, { FC } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';

import { CustomTable } from 'components/modules/CustomTable';
import { UserPill } from 'components/core/UserPill';

import {
  Worker,
  WorkerHealth,
  WorkerStatus,
  WorkerType,
  WorkerDetailsResponse,
} from 'api/workers';

import WorkerDetailsModal from './WorkerDetailsModal';

interface WorkersViewProps {
  workers: Worker[];
  total: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  tabIndex: number;
  statusFilter?: WorkerStatus[];
  typeFilter?: WorkerType[];
  selectedWorker: Worker | null;
  workerDetails?: WorkerDetailsResponse;
  isLoadingDetails: boolean;
  isDetailModalOpen: boolean;
  isResetting: boolean;
  isResolving: boolean;
  isSkipping: boolean;
  isCancelling: boolean;
  health?: WorkerHealth;
  isLoadingHealth: boolean;
  isResuming: boolean;
  onTabChange: (index: number) => void;
  onPageChange: React.Dispatch<React.SetStateAction<number>>;
  onRowClick: (worker: Worker) => void;
  onCloseModal: () => void;
  onRefresh: () => void;
  onResetWorker: (workerId: string) => void;
  onResolveWorker: (workerId: string) => void;
  onSkipBatch: (workerId: string, batchId: string) => void;
  onCancelWorker: (workerId: string) => void;
  onResume: () => void;
  onStatusFilterChange: (statuses: WorkerStatus[] | undefined) => void;
  onTypeFilterChange: (types: WorkerType[] | undefined) => void;
}

const WORKER_TYPE_LABELS: Record<WorkerType, string> = {
  order_delivery: 'Order Delivery',
  create_package: 'Package Creation',
  pickup: 'Pickup',
};

const STATUS_COLORS: Record<WorkerStatus, string> = {
  pending: 'gray',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  paused: 'orange',
  cancelled: 'gray',
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const formatProgress = (worker: Worker) => {
  const percentage = worker.progress || 0;
  const completed = worker.completedUnits || 0;
  const total = worker.totalUnits || 0;
  return `${percentage.toFixed(0)}% (${completed}/${total})`;
};

const truncateError = (error?: { message: string }, maxLength = 50) => {
  if (!error?.message) return '-';
  if (error.message.length <= maxLength) return error.message;
  return `${error.message.substring(0, maxLength)}...`;
};

const WorkersView: FC<WorkersViewProps> = (props) => {
  const {
    workers,
    total,
    totalPages,
    currentPage,
    isLoading,
    tabIndex,
    selectedWorker,
    workerDetails,
    isLoadingDetails,
    isDetailModalOpen,
    isResetting,
    isResolving,
    isSkipping,
    isCancelling,
    health,
    isLoadingHealth,
    isResuming,
    onTabChange,
    onPageChange,
    onRowClick,
    onCloseModal,
    onRefresh,
    onResetWorker,
    onResolveWorker,
    onSkipBatch,
    onCancelWorker,
    onResume,
  } = props;

  const _focus = { border: 'none', backgroundColor: 'primary.500' };
  const _selected = { backgroundColor: 'primary.900', color: 'support.500' };

  const pagination = {
    page: currentPage,
    setPage: onPageChange,
    totalCount: total,
    totalPages,
  };

  const renderWorkerTable = () => (
    <CustomTable
      type="workers"
      data={workers}
      isLoading={isLoading}
      isDeleting={false}
      pagination={pagination}
      onDelete={() => {}}>
      {{
        headerRow: (
          <>
            <Text flex={0.8}>Type</Text>
            <Text flex={0.6}>Status</Text>
            <Text flex={0.6}>Progress</Text>
            <Text flex={0.8}>Created</Text>
            <Text flex={0.8}>Updated</Text>
            <Text flex={1.0}>Error</Text>
          </>
        ),
        renderRow: (worker: Worker) => (
          <Flex flex={1} onClick={() => onRowClick(worker)}>
            <Text flex={0.8}>
              {WORKER_TYPE_LABELS[worker.type] || worker.type}
            </Text>
            <Flex flex={0.6}>
              <Badge colorScheme={STATUS_COLORS[worker.status]}>
                {worker.status.toUpperCase()}
              </Badge>
            </Flex>
            <Text flex={0.6}>{formatProgress(worker)}</Text>
            <Text flex={0.8}>{formatDate(worker.createdAt)}</Text>
            <Text flex={0.8}>{formatDate(worker.updatedAt)}</Text>
            <Text flex={1.0} color={worker.error ? 'red.500' : undefined}>
              {truncateError(worker.error)}
            </Text>
          </Flex>
        ),
        renderEmpty: () => (
          <Center p={8}>
            <Text fontSize="20px">No workers found</Text>
          </Center>
        ),
      }}
    </CustomTable>
  );

  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex justify="space-between">
        <Heading mb={6} fontSize="24px">
          Background Processes
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            <Button
              leftIcon={<MdRefresh size="20px" />}
              onClick={onRefresh}
              isLoading={isLoading}>
              Refresh
            </Button>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>

      {/* Health Banner */}
      {isLoadingHealth ? (
        <Flex justify="center" py={3}>
          <Spinner size="sm" color="primary.400" />
        </Flex>
      ) : health ? (
        <Alert
          status={health.active ? 'success' : 'error'}
          borderRadius="8px"
          mb={4}
          justifyContent="space-between"
          alignItems="center">
          <Flex align="center">
            <AlertIcon />
            <Flex direction="column">
              <AlertTitle fontSize="14px">
                Batch Processing: {health.active ? 'Active' : 'Inactive'}
              </AlertTitle>
              {!health.active && (
                <AlertDescription fontSize="12px">
                  Consecutive failures: {health.consecutiveFailures}/
                  {health.maxFailures}
                  {health.lastFailureError && (
                    <> &mdash; {health.lastFailureError}</>
                  )}
                </AlertDescription>
              )}
              {health.active && health.consecutiveFailures > 0 && (
                <AlertDescription fontSize="12px">
                  Consecutive failures: {health.consecutiveFailures}/
                  {health.maxFailures}
                </AlertDescription>
              )}
            </Flex>
          </Flex>
          {!health.active && (
            <Button
              size="sm"
              colorScheme="green"
              isLoading={isResuming}
              onClick={onResume}>
              Resume Processing
            </Button>
          )}
        </Alert>
      ) : null}

      <Tabs
        variant="enclosed"
        size="md"
        index={tabIndex}
        onChange={onTabChange}>
        <TabList _selected={_selected} _focus={_focus} w="100%">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All Workers
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Paused Workers
          </Tab>
        </TabList>
        <TabPanels p={0}>
          <TabPanel p={0}>{renderWorkerTable()}</TabPanel>
          <TabPanel p={0}>{renderWorkerTable()}</TabPanel>
        </TabPanels>
      </Tabs>

      <WorkerDetailsModal
        isOpen={isDetailModalOpen}
        onClose={onCloseModal}
        worker={selectedWorker}
        details={workerDetails}
        isLoading={isLoadingDetails}
        isResetting={isResetting}
        isResolving={isResolving}
        isSkipping={isSkipping}
        isCancelling={isCancelling}
        onResetWorker={onResetWorker}
        onResolveWorker={onResolveWorker}
        onSkipBatch={onSkipBatch}
        onCancelWorker={onCancelWorker}
      />
    </Flex>
  );
};

export default WorkersView;
